import type { HeroSectionMode } from "@/pages/USDv/components/HeroSection";

// Right-column variant when the wallet is connected but has no active
// YaaS position. Drives both the right-column component (BuyWidget vs
// YaasEarnProjectionCTA) and the left-column HeroSection mode.
//
//   "projection"        → wallet holds USDv; show enroll projection (State 3)
//   "below-min"         → holds USDv, but under the minimum to earn
//   "swap-from-stables" → wallet holds USDC/USDT but no USDv (State 2)
//   "swap-no-stables"   → wallet holds nothing useful (State 1)
//
// "below-min" is set by `useResolvedRetailView`, not by
// `computeLandingVariant`: the minimum is policy the resolver holds and
// this helper does not. It keeps the right column's projection card (it
// is not a swap variant) but takes the "Get USDv" hero, because a wallet
// under the floor needs MORE USDv, and "Your USDv can earn" tells someone
// who cannot yet earn that they already can.
//
// Keep the first load distinct from a known balance. A background refresh
// keeps the current surface mounted so the selected swap token survives.
export type LandingVariant = "loading" | "projection" | "below-min" | "swap-from-stables" | "swap-no-stables";

export function computeLandingVariant({
    usdvBalance,
    stablesBalance,
    isBalancesLoading,
    hasLoadedBalances = !isBalancesLoading
}: {
    usdvBalance: number;
    stablesBalance: number;
    isBalancesLoading: boolean;
    /** True once balances have been fetched at least once this session. */
    hasLoadedBalances?: boolean;
}): LandingVariant {
    if (!hasLoadedBalances) return "loading";
    if (usdvBalance > 0) return "projection";
    if (stablesBalance > 0) return "swap-from-stables";
    return "swap-no-stables";
}

// HeroSection mode for a given landing variant. Kept as a thin map so
// the JSX render sites stay readable and the variant→mode mapping is
// the single source of truth (mobile + desktop pick the same mode).
export function heroModeForVariant(variant: LandingVariant): HeroSectionMode {
    switch (variant) {
        // Under the minimum: the ask is to get more USDv, not to enrol
        // what is already there.
        case "loading":
        case "swap-no-stables":
        case "below-min":
            return "default";
        case "swap-from-stables":
            return "swap-to-usdv";
        case "projection":
            return "usdv-can-earn";
    }
}
